import PropTypes from 'prop-types';
// CORE
import React, { useState, useMemo } from 'react';

import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import QButton from 'components/QButton';
import { boxShadowsToDropShadows } from 'themes/createTheme';

const arrowSize = 12;
const useStyles = makeStyles()((theme) => ({
  popper: {
    zIndex: theme.zIndex.modal,
    filter: boxShadowsToDropShadows(theme.shadows[16]),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.contrastText,
    borderRadius: theme.shape.borderRadius * 4,
    width: 450,
    padding: theme.spacing(2),
    '&[data-popper-placement*="left"] .arrow': {
      right: 0,
      marginRight: -2 * arrowSize,
      marginTop: theme.shape.borderRadius,
      marginBottom: theme.shape.borderRadius,
      borderTopColor: 'transparent',
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    '&[data-popper-placement*="top"] .arrow': {
      bottom: 0,
      marginBottom: -2 * arrowSize + 1,
      marginLeft: theme.shape.borderRadius,
      marginRight: theme.shape.borderRadius,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    '&[data-popper-placement*="right"] .arrow': {
      left: 0,
      marginLeft: -2 * arrowSize,
      marginTop: theme.shape.borderRadius,
      marginBottom: theme.shape.borderRadius,
      borderLeftColor: 'transparent',
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    '&[data-popper-placement*="bottom"] .arrow': {
      top: 0,
      marginTop: -2 * arrowSize + 1,
      marginLeft: theme.shape.borderRadius,
      marginRight: theme.shape.borderRadius,
      borderLeftColor: 'transparent',
      borderTopColor: 'transparent',
      borderRightColor: 'transparent',
    },
  },
  arrow: {
    zIndex: theme.zIndex.tooltip,
    position: 'absolute',
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    borderWidth: arrowSize,
  },
  button: {
    color: theme.palette.text.contrastText,
    borderColor: theme.palette.text.contrastText,
  },
}));

const centerArrowHorizontally = ({ popper }) => popper.width / 2;
const centerArrowVertically = ({ popper }) => popper.height / 2;

const getArrowPaddingFunction = (centerArrow, placement) => {
  if (centerArrow && placement) {
    if (placement.match(/(bottom|top)-(start|end)/)) {
      return centerArrowHorizontally;
    }

    if (placement.match(/(left|right)-(start|end)/)) {
      return centerArrowVertically;
    }
  }

  return undefined;
};

const GuidedTooltip = (props) => {
  const {
    centerArrow = false,
    onClickAway,
    anchorEl = null,
    open = false,
    className,
    modifiers: ferryModifiers = [],
    placement,
    title,
    body,
    cancelLabel,
    continueLabel,
    onCancel,
    onContinue,
    noArrow,
    style,
    ...otherProps
  } = props;
  const { classes, cx } = useStyles();

  const [arrowRef, setArrowRef] = useState();

  const content = useMemo(
    () => (
      <Box display='flex' flexDirection='column' alignItems='flex-start'>
        {!noArrow && <div className={cx(classes.arrow, 'arrow')} ref={(ref) => setArrowRef(ref)} />}
        <Typography variant='bodyMedium' color='inherit'>
          {title}
        </Typography>
        <Typography variant='paragraphRegular' color='inherit'>
          {body}
        </Typography>
        <Box width='100%' display='flex' alignItems='center' justifyContent='flex-end' mt={1}>
          {cancelLabel && (
            <QButton className={classes.button} onClick={onCancel}>
              {cancelLabel}
            </QButton>
          )}
          {continueLabel && (
            <QButton className={classes.button} variant='outlined' onClick={onContinue}>
              {continueLabel}
            </QButton>
          )}
        </Box>
      </Box>
    ),
    [noArrow, body, cancelLabel, classes, continueLabel, onCancel, onContinue, title, cx],
  );

  const modifiers = useMemo(
    () => [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'right', 'bottom', 'left'],
        },
      },
      {
        name: 'arrow',
        options: {
          element: arrowRef,
          padding: getArrowPaddingFunction(centerArrow, placement),
        },
      },
      ...ferryModifiers,
    ],
    [ferryModifiers, centerArrow, placement, arrowRef],
  );

  return anchorEl && open ? (
    <Popper
      className={cx(classes.popper, className)}
      keepMounted={false}
      sharedcomponentid={'GUIDED_TOOLTIP'}
      placement={placement}
      open={open}
      anchorEl={anchorEl}
      modifiers={modifiers}
      style={{ margin: '8px', ...(style || {}) }}
      {...otherProps}
    >
      {onClickAway ? <ClickAwayListener onClickAway={onClickAway}>{content}</ClickAwayListener> : content}
    </Popper>
  ) : null;
};

GuidedTooltip.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  modifiers: PropTypes.arrayOf(PropTypes.object),
  placement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  title: PropTypes.string,
  body: PropTypes.string,
  cancelLabel: PropTypes.string,
  continueLabel: PropTypes.string,
  onCancel: PropTypes.func,
  onContinue: PropTypes.func,
  onClickAway: PropTypes.func,
  noArrow: PropTypes.bool,
  centerArrow: PropTypes.bool,
};

export default GuidedTooltip;
